/**
 * usePlanFeatures — reads the clinic's plan from auth context
 * and returns feature flags for frontend gating.
 */
import { useAuth } from '@/contexts/AuthContext';

export type PlanType = 'checker_starter' | 'checker_growth' | 'solo' | 'team' | 'clinic' | 'enterprise' | 'trialing';

export interface PlanFeatures {
  plan: PlanType;
  planStatus: string;
  practitionersAllowed: number;
  hasFullClinical: boolean;
  hasCheckerFree: boolean;
  checkerChecksLimit: number;
  hasAiReports: boolean;
  hasExerciseLibrary: boolean;
  hasAnalytics: boolean;
  hasSuperAdmin: boolean;
  isCheckerOnly: boolean;
}

const PLAN_FEATURES: Record<PlanType, Omit<PlanFeatures, 'plan' | 'planStatus' | 'isCheckerOnly'>> = {
  checker_starter:  { practitionersAllowed: 0, hasFullClinical: false, hasCheckerFree: false, checkerChecksLimit: 500, hasAiReports: false, hasExerciseLibrary: false, hasAnalytics: false, hasSuperAdmin: false },
  checker_growth:   { practitionersAllowed: 0, hasFullClinical: false, hasCheckerFree: false, checkerChecksLimit: 0,   hasAiReports: false, hasExerciseLibrary: false, hasAnalytics: false, hasSuperAdmin: false },
  solo:             { practitionersAllowed: 1, hasFullClinical: true,  hasCheckerFree: true,  checkerChecksLimit: 0,   hasAiReports: true,  hasExerciseLibrary: true,  hasAnalytics: true,  hasSuperAdmin: false },
  team:             { practitionersAllowed: 3, hasFullClinical: true,  hasCheckerFree: true,  checkerChecksLimit: 0,   hasAiReports: true,  hasExerciseLibrary: true,  hasAnalytics: true,  hasSuperAdmin: false },
  clinic:           { practitionersAllowed: 10, hasFullClinical: true, hasCheckerFree: true,  checkerChecksLimit: 0,   hasAiReports: true,  hasExerciseLibrary: true,  hasAnalytics: true,  hasSuperAdmin: false },
  enterprise:       { practitionersAllowed: 999, hasFullClinical: true, hasCheckerFree: true, checkerChecksLimit: 0,   hasAiReports: true,  hasExerciseLibrary: true,  hasAnalytics: true,  hasSuperAdmin: true },
  trialing:         { practitionersAllowed: 999, hasFullClinical: true, hasCheckerFree: true, checkerChecksLimit: 0,   hasAiReports: true,  hasExerciseLibrary: true,  hasAnalytics: true,  hasSuperAdmin: false },
};

export function usePlanFeatures(): PlanFeatures {
  const { user } = useAuth();

  // Get plan from user context (added to /auth/me response)
  const plan = ((user as any)?.plan ?? 'solo') as PlanType;
  const planStatus = ((user as any)?.planStatus ?? 'trialing') as string;

  const features = PLAN_FEATURES[plan] ?? PLAN_FEATURES.solo;
  const isCheckerOnly = plan === 'checker_starter' || plan === 'checker_growth';

  return {
    plan,
    planStatus,
    ...features,
    isCheckerOnly,
  };
}
